/**
 * Motion templates (W2-05 easing-templates-raw; DESIGN.md §4.13, §6.6; contract `ToolsApi['applyTemplate']` in
 * core/tools/types.ts): fade in / out, slide in / out, move to a point, spin, swing, pulse, shake (seeded), flash a
 * colour and orbit the parent. A template writes ORDINARY keys on the line, built only from `Core.keys` commands
 * (ensureTrack, addLayer, insertKey, deleteKeys, unlinkSegments, setEase, setKeyValues), so every motion rule holds:
 * the lead-in Stay key at beat 0, holds arriving as jumps, link groups, value types.
 *
 * X/Y-linked lines (the default for new lines): the link mirrors every key insertion, deletion and curve change of
 * one position axis onto the other, which would let the second axis' plan overwrite the first one's. The template
 * unlinks the axes inside its transaction, writes each axis exactly as on an unlinked line, links them again and
 * gives both axes keys at each other's beats in the span (shape-preserving; each axis keeps its own values and
 * curves, e.g. the In Sine / Out Sine pieces of an orbit). A template that moves one axis leaves the other axis'
 * motion as it was.
 *
 * Two modes:
 *  - base (default): absolute values on layer 0 of the property. The span [at, at + duration] is replaced: keys inside
 *    it are removed, the span edges are split shape-preservingly first, so the curve before `at` is untouched.
 *    Imported lead-ins (Phira continues a track's first segment backwards before a first key after beat 0) are made
 *    explicit first when the span starts at or before that key (`explicitLeadIns`, as Core's bake, reverse and paste
 *    do), otherwise the template's own first segment would be the one Phira continues before the span.
 *    "State" templates (fade in / out, slide out, move to, spin, orbit) keep their end value after the span: the
 *    segment after the span becomes "Stay, then jump" until the next existing key. "Return" templates (slide in,
 *    swing, pulse, shake, flash) end on the original curve's value at the span end, so the curve after the span is
 *    unchanged.
 *    A template that creates a property without the lead-in rule (flash on a line without colour keys) also holds
 *    the colour it returns to from beat 0, so the line looks the same before and after the span.
 *  - asNewLayer (Full level, summed properties only): the motion is written as OFFSETS on a fresh additive layer
 *    (`Core.keys.addLayer`: a Stay key of 0 at beat 0), leaving every existing key untouched. Offsets that persist a
 *    value computed at the span end (fade out, slide out, move to) get a note when the layers under them change
 *    later (the sum follows those changes; opacity below 0 also hides the line's notes in Phira).
 *
 * Exact shapes where Phira allows them: a swing is a sequence of quarter sine pieces (Out Sine away from the centre,
 * In Sine back), which is an exact sinusoid; an orbit is cut at quarter turns and each piece of X and Y is a slice of
 * In Sine / Out Sine (Phira renormalises a slice exactly), so the line stays on the circle whatever the tempo. The
 * circle is round ON SCREEN: it is built in isotropic units (Phira maps moveY through 1 / ar, rendering.md §3.1).
 * Shake offsets come from a seeded generator, so a preview equals the applied result.
 *
 * Failure reasons and notes use the message namespace 'templates' (English text in
 * src/features/easing/templates.messages.ts; see change request W2-05 CR-1).
 *
 * Coupling to Core internals (change request CR-W2-05-5, option B): writing goes through `Core.*` commands only,
 * but two steps have no command in the contract and use the motion machinery through its stable surface
 * `core/commands/kit.ts` (a change there needs a change request), exactly as Core's own paste / reverse / bake and
 * the other batch tools (flatten, fit, clone, motionPath, distribute) do:
 *  - the imported lead-in fix (`explicitLeadIns`): motionTime `explicitLeadIn` on motionTrack `readKeys` /
 *    `commitKeys`, its split notes through motionNotes `collectSplit` / `worseSplitOn` / `noteWorstSplit`;
 *  - the X/Y relink (`relinkXy`): link `alignXyBeats` / `alignXyAll` / `cleanLinks`, the other axis' split note
 *    through motionNotes `notePartnerSplit` (splitKey `worseSplit` / `EXACT_SPLIT`).
 * Reading uses motionTrack `ownerInfo` / `neutralValue` / `tidy`, splitKey `curveCtx` / `valueAtBeat` and layers
 * `MAX_UI_LAYERS`. A signature change there shows up in `pnpm typecheck` on this file; a behaviour change is caught
 * by templates.test.ts ("imported lead-ins", "X/Y-linked lines", every template's full-invariant run), which
 * compares against the reference evaluator and an unlinked twin rather than against these helpers.
 */

import { Core } from '../commands/index';
import {
  alignXyAll,
  alignXyBeats,
  cleanLinks,
  collectSplit,
  commitKeys,
  curveCtx,
  EXACT_SPLIT,
  explicitLeadIn,
  MAX_UI_LAYERS,
  neutralValue,
  notePartnerSplit,
  noteWorstSplit,
  ownerInfo,
  readKeys,
  tidy,
  valueAtBeat,
  worseSplit,
  worseSplitOn,
  type SplitOn,
  type SplitReport,
  type TrackInfo,
} from '../commands/kit';
import { Ease as EaseApi, HOLD, LINEAR } from '../ease/index';
import type { Ease } from '../ease/types';
import { createRng } from '../math/rng';
import { Defaults } from '../model/defaults';
import { isUiMover, kindOfLine } from '../model/lineKind';
import { PROP_SPECS } from '../model/props';
import type { KeyId, Line, LineId, LineKind, LineProp, TrackId, TrackValue } from '../model/types';
import type { TxBuilder } from '../ops/types';
import { Beats } from '../time/beat';
import { formatBeat } from '../time/beatSyntax';
import type { Beat } from '../time/types';
import type { MessageRef, RGB } from '../util/types';
import type { TemplateId, TemplateParams, ToolReport, ToolsApi } from './types';

// ---------------------------------------------------------------------------------------------------------------
// Catalogue
// ---------------------------------------------------------------------------------------------------------------

/** Message keys produced by the templates (namespace 'templates'). */
export const TEMPLATE_MSG = {
  lineMissing: 'templates.reason.lineMissing',
  durationInvalid: 'templates.reason.durationInvalid',
  notLayered: 'templates.reason.notLayered',
  paramInvalid: 'templates.reason.paramInvalid',
  colorInvalid: 'templates.reason.colorInvalid',
  pointMissing: 'templates.reason.pointMissing',
  thicknessSkipped: 'templates.note.thicknessSkipped',
  colorFixed: 'templates.note.colorFixed',
  noParent: 'templates.note.noParent',
  xyLinked: 'templates.note.xyLinked',
  otherLayers: 'templates.note.otherLayers',
  baseChanges: 'templates.note.baseChanges',
  alphaNegative: 'templates.note.alphaNegative',
} as const;

export const TEMPLATE_IDS: readonly TemplateId[] = [
  'fadeIn',
  'fadeOut',
  'slideIn',
  'slideOut',
  'moveTo',
  'spin',
  'swing',
  'pulse',
  'shake',
  'flash',
  'orbitParent',
];

export type SlideSide = 'left' | 'right' | 'top' | 'bottom' | 'point';
export type ShakeAxes = 'both' | 'x' | 'y';

export interface TemplateSpec {
  readonly id: TemplateId;
  /** Properties the template writes. */
  readonly props: readonly LineProp[];
  /** "Apply as a new layer" is possible (every written property is a summed property). */
  readonly layered: boolean;
  /** The template's curve (`TemplateParams.ease`) shapes the motion. */
  readonly usesEase: boolean;
  /** The end value stays after the span ('persist') or the original curve resumes ('resume'). */
  readonly after: 'persist' | 'resume';
  /** Default parameter values (the dialog starts from these). */
  readonly defaults: Readonly<Record<string, number | string | boolean>>;
}

/** Default slide distances: one screen width / height in RPE px. */
export const SLIDE_DISTANCE_X = 1350;
export const SLIDE_DISTANCE_Y = 900;

/** Phira's default Perfect colour (respack colorPerfect without alpha): a normal line's colour in autoplay. */
export const DEFAULT_LINE_COLOR: RGB = [255, 236, 159];

export const TEMPLATE_SPECS: Readonly<Record<TemplateId, TemplateSpec>> = {
  fadeIn: {
    id: 'fadeIn',
    props: ['alpha'],
    layered: true,
    usesEase: true,
    after: 'persist',
    defaults: { from: 0, to: 255 },
  },
  fadeOut: { id: 'fadeOut', props: ['alpha'], layered: true, usesEase: true, after: 'persist', defaults: { to: 0 } },
  slideIn: {
    id: 'slideIn',
    props: ['moveX', 'moveY'],
    layered: true,
    usesEase: true,
    after: 'resume',
    defaults: { from: 'left', distance: SLIDE_DISTANCE_X, x: 0, y: 0 },
  },
  slideOut: {
    id: 'slideOut',
    props: ['moveX', 'moveY'],
    layered: true,
    usesEase: true,
    after: 'persist',
    defaults: { to: 'right', distance: SLIDE_DISTANCE_X, x: 0, y: 0 },
  },
  moveTo: {
    id: 'moveTo',
    props: ['moveX', 'moveY'],
    layered: true,
    usesEase: true,
    after: 'persist',
    defaults: { x: 0, y: 0 },
  },
  spin: {
    id: 'spin',
    props: ['rotate'],
    layered: true,
    usesEase: true,
    after: 'persist',
    defaults: { turns: 1, dir: 1 },
  },
  swing: {
    id: 'swing',
    props: ['rotate'],
    layered: true,
    usesEase: false,
    after: 'resume',
    defaults: { amplitude: 15, swings: 2, dir: 1, settle: true },
  },
  pulse: {
    id: 'pulse',
    props: ['scaleX', 'scaleY'],
    layered: false,
    usesEase: true,
    after: 'resume',
    defaults: { amount: 1.25, count: 1, axes: 'both' },
  },
  shake: {
    id: 'shake',
    props: ['moveX', 'moveY'],
    layered: true,
    usesEase: false,
    after: 'resume',
    defaults: { amplitude: 20, count: 8, axes: 'both', seed: 1, settle: false },
  },
  flash: {
    id: 'flash',
    props: ['color'],
    layered: false,
    usesEase: true,
    after: 'resume',
    defaults: { color: '#ffffff' },
  },
  orbitParent: {
    id: 'orbitParent',
    props: ['moveX', 'moveY'],
    layered: true,
    usesEase: false,
    after: 'persist',
    defaults: { turns: 1, dir: 1, radius: 0 },
  },
};

/** Default ease of the templates that use one ("Soft, slow end": Out Quad). */
export const TEMPLATE_DEFAULT_EASE: Ease = { kind: 'tween', id: 4, left: 0, right: 1 };

// ---------------------------------------------------------------------------------------------------------------
// Parameters
// ---------------------------------------------------------------------------------------------------------------

type Params = TemplateParams['params'];

function fail(tx: TxBuilder, key: string, params?: Readonly<Record<string, string | number>>): never {
  return tx.fail(params === undefined ? { key } : { key, params });
}

function num(tx: TxBuilder, p: Params, key: string, def: number, check?: (v: number) => boolean): number {
  const raw = p[key];
  const v = raw === undefined ? def : typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : NaN;
  if (!Number.isFinite(v) || (check !== undefined && !check(v))) fail(tx, TEMPLATE_MSG.paramInvalid, { name: key });
  return v;
}

function choice<T extends string>(tx: TxBuilder, p: Params, key: string, def: T, allowed: readonly T[]): T {
  const raw = p[key];
  if (raw === undefined) return def;
  const hit = allowed.find((a) => a === raw);
  return hit ?? fail(tx, TEMPLATE_MSG.paramInvalid, { name: key });
}

function flag(p: Params, key: string, def: boolean): boolean {
  const raw = p[key];
  return typeof raw === 'boolean' ? raw : raw === undefined ? def : raw === 1 || raw === 'true';
}

/** Spin / swing / orbit direction: 1 = clockwise on screen, -1 = counter-clockwise. */
function direction(tx: TxBuilder, p: Params): 1 | -1 {
  const d = num(tx, p, 'dir', 1, (v) => v === 1 || v === -1);
  return d === -1 ? -1 : 1;
}

/** '#rrggbb', '#rgb' or 'r,g,b' (0..255) -> RGB; null when malformed. */
export function parseTemplateColor(s: string): RGB | null {
  const t = s.trim();
  const hex = /^#?([0-9a-f]{6}|[0-9a-f]{3})$/i.exec(t);
  if (hex !== null) {
    const h = hex[1] ?? '';
    const full = h.length === 3 ? h.replace(/./g, (c) => c + c) : h;
    return [parseInt(full.slice(0, 2), 16), parseInt(full.slice(2, 4), 16), parseInt(full.slice(4, 6), 16)];
  }
  const parts = t.split(/[\s,]+/).filter((x) => x !== '');
  if (parts.length !== 3) return null;
  const vals = parts.map(Number);
  if (vals.some((v) => !Number.isInteger(v) || v < 0 || v > 255)) return null;
  return [vals[0] ?? 0, vals[1] ?? 0, vals[2] ?? 0];
}

/** '#rrggbb' of an RGB value. */
export function formatTemplateColor(c: RGB): string {
  return `#${c.map((v) => Math.round(v).toString(16).padStart(2, '0')).join('')}`;
}

// ---------------------------------------------------------------------------------------------------------------
// Reading the line
// ---------------------------------------------------------------------------------------------------------------

interface LineCtx {
  readonly rec: Line;
  readonly kind: LineKind;
  readonly uiMover: boolean;
}

function lineCtx(tx: TxBuilder, line: LineId): LineCtx {
  const rec = tx.doc.lines.get(line) ?? fail(tx, TEMPLATE_MSG.lineMissing);
  return { rec, kind: kindOfLine(tx.doc, tx.idx, rec), uiMover: isUiMover(rec) };
}

function infoOf(tx: TxBuilder, line: LineId, prop: LineProp, layer: number): { info: TrackInfo; exists: boolean } {
  return ownerInfo(tx, { kind: 'line', line, prop, layer }) ?? fail(tx, TEMPLATE_MSG.lineMissing);
}

/** Value of one layer of a property at beat t (after a key: its leave value); an absent layer looks like itself. */
function layerValue(tx: TxBuilder, line: LineId, prop: LineProp, layer: number, t: Beat): TrackValue {
  const o = infoOf(tx, line, prop, layer);
  if (!o.exists) return neutralValue(o.info);
  return valueAtBeat(readKeys(tx, o.info.track.id), t, curveCtx(tx, o.info));
}

function numberAt(tx: TxBuilder, line: LineId, prop: LineProp, layer: number, t: Beat): number {
  const v = layerValue(tx, line, prop, layer, t);
  return typeof v === 'number' ? v : 0;
}

/** Layers of a summed property that have a track. */
function layersOf(tx: TxBuilder, line: LineId, prop: LineProp): number[] {
  const out: number[] = [];
  for (const id of tx.idx.tracksOfLine(line).get(prop) ?? []) {
    const t = tx.doc.tracks.get(id);
    if (t?.owner.kind === 'line') out.push(t.owner.layer);
  }
  return out;
}

/** Sum of the given layers of a property at beat t (the absent layer 0 counts as its neutral value). */
function sumLayers(tx: TxBuilder, line: LineId, prop: LineProp, t: Beat, skip: number | null): number {
  const layers = new Set(layersOf(tx, line, prop));
  layers.add(0);
  let sum = 0;
  for (const layer of layers) if (layer !== skip) sum += numberAt(tx, line, prop, layer, t);
  return sum;
}

// ---------------------------------------------------------------------------------------------------------------
// Plans
// ---------------------------------------------------------------------------------------------------------------

/** A key of a span. `vIn` (interior keys only) makes a jump: arrive at `vIn`, leave from `vOut`. */
interface PlanPoint {
  readonly t: Beat;
  readonly vIn?: TrackValue;
  readonly vOut: TrackValue;
}

interface SpanPlan {
  readonly prop: LineProp;
  /** Points in time order; the first at `at`, the last at the span end. */
  readonly points: readonly PlanPoint[];
  /** Ease of each segment (points.length - 1). */
  readonly eases: readonly Ease[];
  readonly after: 'persist' | 'resume';
  /**
   * When the template creates the track: the value held from beat 0 up to the span (a Stay key at beat 0), so the
   * line looks the same before the span as after it (a property without the lead-in rule would otherwise show
   * Phira's event default before its first key).
   */
  readonly leadIn?: TrackValue;
}

interface Built {
  readonly plans: readonly SpanPlan[];
  readonly notes: readonly MessageRef[];
  readonly maxError?: number;
}

interface BuildCtx {
  readonly tx: TxBuilder;
  readonly p: TemplateParams;
  readonly line: LineCtx;
  readonly at: Beat;
  readonly end: Beat;
  /** Offsets on a new layer instead of absolute layer-0 values. */
  readonly relative: boolean;
  readonly ease: Ease;
}

/** Evenly spaced beats at + k * duration / n, k = 0..n (exact rationals). */
function evenBeats(tx: TxBuilder, at: Beat, duration: Beat, n: number): Beat[] {
  const out: Beat[] = [];
  for (let k = 0; k <= n; k++)
    out.push(
      k === n
        ? Beats.add(at, duration, tx.rounding)
        : Beats.add(at, Beats.mulRat(duration, k, n, tx.rounding), tx.rounding),
    );
  return out;
}

/** A computed value as stored (float noise removed). */
const n0 = (v: number): number => tidy(v);

function twoPoint(
  c: BuildCtx,
  prop: LineProp,
  from: TrackValue,
  to: TrackValue,
  after: 'persist' | 'resume',
): SpanPlan {
  return {
    prop,
    points: [
      { t: c.at, vOut: from },
      { t: c.end, vOut: to },
    ],
    eases: [c.ease],
    after,
  };
}

function buildFade(c: BuildCtx, out: boolean): Built {
  const { tx, p } = c;
  const line = c.line.rec.id;
  if (!out) {
    const from = num(tx, p.params, 'from', 0);
    const to = num(tx, p.params, 'to', 255);
    if (c.relative) {
      // Offset from (from - the line's opacity at the start) up to 0: the sum goes from `from` to the line's own.
      const base = sumLayers(tx, line, 'alpha', c.at, null);
      return { plans: [twoPoint(c, 'alpha', n0(from - base), 0, 'resume')], notes: [] };
    }
    return { plans: [twoPoint(c, 'alpha', n0(from), n0(to), 'persist')], notes: otherLayersNote(c, ['alpha']) };
  }
  const to = num(tx, p.params, 'to', 0);
  if (c.relative) {
    const base = sumLayers(tx, line, 'alpha', c.end, null);
    return { plans: [twoPoint(c, 'alpha', 0, n0(to - base), 'persist')], notes: driftNotes(c, ['alpha'], to) };
  }
  const start = numberAt(tx, line, 'alpha', 0, c.at);
  return { plans: [twoPoint(c, 'alpha', start, n0(to), 'persist')], notes: otherLayersNote(c, ['alpha']) };
}

/** How the existing layers of a property move after the span end (see `baseDrift`). */
interface BaseDrift {
  /** Beat from which their sum leaves its value at the span end. */
  readonly from: Beat;
  /** Lowest (sum - sum at the span end) seen after the span. */
  readonly lowest: number;
}

const DRIFT_SAMPLES = 4;

/**
 * Whether the existing layers of a summed property keep their sum at the span end afterwards (null) or change
 * again. An offset layer that PERSISTS a value computed at the span end ("Apply as a new layer" for fade out, slide
 * out and move to) only keeps its promise while the layers under it stay put. Sampled at every key after the span
 * and inside every gap between them.
 */
function baseDrift(c: BuildCtx, prop: LineProp): BaseDrift | null {
  const { tx } = c;
  const line = c.line.rec.id;
  const sum = (t: Beat): number => sumLayers(tx, line, prop, t, null);
  const atEnd = sum(c.end);
  const beats: Beat[] = [];
  for (const layer of layersOf(tx, line, prop)) {
    const o = infoOf(tx, line, prop, layer);
    if (!o.exists) continue;
    for (const k of readKeys(tx, o.info.track.id)) if (Beats.lt(c.end, k.t)) beats.push(k.t);
  }
  beats.sort((a, b) => Beats.cmp(a, b));
  const tol = 1e-6 * Math.max(1, Math.abs(atEnd));
  let from: Beat | null = null;
  let lowest = 0;
  let prev = c.end;
  for (const b of beats) {
    if (!Beats.lt(prev, b)) continue;
    const gap = Beats.sub(b, prev, tx.rounding);
    for (let j = 1; j <= DRIFT_SAMPLES; j++) {
      const t =
        j === DRIFT_SAMPLES ? b : Beats.add(prev, Beats.mulRat(gap, j, DRIFT_SAMPLES, tx.rounding), tx.rounding);
      const d = sum(t) - atEnd;
      if (Math.abs(d) <= tol) continue;
      // A change inside the gap starts at its left key; a jump at the key itself starts there.
      from ??= j === DRIFT_SAMPLES ? b : prev;
      lowest = Math.min(lowest, d);
    }
    prev = b;
  }
  return from === null ? null : { from, lowest };
}

/**
 * Notes for offset layers that persist a value computed at the span end while the layers under them change later:
 * the sum then follows those changes. For opacity, a sum that drops below 0 also hides the line's notes in Phira
 * (rendering.md §3.3), which gets its own note. `target` is the opacity the fade promises (alpha only).
 */
function driftNotes(c: BuildCtx, props: readonly LineProp[], target?: number): MessageRef[] {
  let first: BaseDrift | null = null;
  let negative = false;
  for (const prop of props) {
    const d = baseDrift(c, prop);
    if (d === null) continue;
    if (first === null || Beats.lt(d.from, first.from)) first = d;
    if (prop === 'alpha' && target !== undefined && target + d.lowest < 0) negative = true;
  }
  if (first === null) return [];
  const params = { beat: formatBeat(first.from) };
  return [{ key: negative ? TEMPLATE_MSG.alphaNegative : TEMPLATE_MSG.baseChanges, params }];
}

function otherLayersNote(c: BuildCtx, props: readonly LineProp[]): MessageRef[] {
  return props.some((prop) => layersOf(c.tx, c.line.rec.id, prop).some((l) => l > 0))
    ? [{ key: TEMPLATE_MSG.otherLayers }]
    : [];
}

const SIDES: readonly SlideSide[] = ['left', 'right', 'top', 'bottom', 'point'];

/** Offset of a slide side: [dx, dy] away from the target (left = -x, top = +y; Y is up in RPE). */
function sideOffset(side: Exclude<SlideSide, 'point'>, distance: number): readonly [number, number] {
  switch (side) {
    case 'left':
      return [-distance, 0];
    case 'right':
      return [distance, 0];
    case 'top':
      return [0, distance];
    case 'bottom':
      return [0, -distance];
  }
}

function point(tx: TxBuilder, p: Params): readonly [number, number] {
  if (p.x === undefined || p.y === undefined) fail(tx, TEMPLATE_MSG.pointMissing);
  return [num(tx, p, 'x', 0), num(tx, p, 'y', 0)];
}

function buildSlide(c: BuildCtx, into: boolean): Built {
  const { tx, p } = c;
  const line = c.line.rec.id;
  const side = choice(tx, p.params, into ? 'from' : 'to', into ? 'left' : 'right', SIDES);
  const axes: readonly ('moveX' | 'moveY')[] =
    side === 'left' || side === 'right'
      ? ['moveX']
      : side === 'top' || side === 'bottom'
        ? ['moveY']
        : ['moveX', 'moveY'];
  const offsets: readonly [number, number] =
    side === 'point'
      ? [0, 0]
      : sideOffset(
          side,
          num(
            tx,
            p.params,
            'distance',
            side === 'left' || side === 'right' ? SLIDE_DISTANCE_X : SLIDE_DISTANCE_Y,
            (v) => v >= 0,
          ),
        );
  const target = side === 'point' ? point(tx, p.params) : null;
  const plans: SpanPlan[] = [];
  axes.forEach((prop) => {
    const ai = prop === 'moveX' ? 0 : 1;
    const off = offsets[ai];
    if (into) {
      // From the side (or the point) to where the line is at the end of the span.
      if (c.relative) {
        const start = target === null ? off : target[ai] - sumLayers(tx, line, prop, c.at, null);
        plans.push(twoPoint(c, prop, n0(start), 0, 'resume'));
      } else {
        const goal = numberAt(tx, line, prop, 0, c.end);
        const start = target === null ? goal + off : target[ai] - sumLayers(tx, line, prop, c.at, 0);
        plans.push(twoPoint(c, prop, n0(start), goal, 'resume'));
      }
    } else if (c.relative) {
      const goal = target === null ? off : target[ai] - sumLayers(tx, line, prop, c.end, null);
      plans.push(twoPoint(c, prop, 0, n0(goal), 'persist'));
    } else {
      const start = numberAt(tx, line, prop, 0, c.at);
      const goal = target === null ? start + off : target[ai] - sumLayers(tx, line, prop, c.end, 0);
      plans.push(twoPoint(c, prop, start, n0(goal), 'persist'));
    }
  });
  return { plans, notes: c.relative ? (into ? [] : driftNotes(c, axes)) : otherLayersNote(c, axes) };
}

function buildMoveTo(c: BuildCtx): Built {
  const { tx, p } = c;
  const line = c.line.rec.id;
  const target = point(tx, p.params);
  const plans = (['moveX', 'moveY'] as const).map((prop) => {
    const want = prop === 'moveX' ? target[0] : target[1];
    if (c.relative) {
      const goal = want - sumLayers(tx, line, prop, c.end, null);
      return twoPoint(c, prop, 0, n0(goal), 'persist');
    }
    const start = numberAt(tx, line, prop, 0, c.at);
    const goal = want - sumLayers(tx, line, prop, c.end, 0);
    return twoPoint(c, prop, start, n0(goal), 'persist');
  });
  return { plans, notes: c.relative ? driftNotes(c, ['moveX', 'moveY']) : [] };
}

function buildSpin(c: BuildCtx): Built {
  const { tx, p } = c;
  const turns = num(tx, p.params, 'turns', 1, (v) => v > 0 && v <= 1000);
  const delta = 360 * turns * direction(tx, p.params);
  if (c.relative) return { plans: [twoPoint(c, 'rotate', 0, n0(delta), 'persist')], notes: [] };
  const start = numberAt(tx, c.line.rec.id, 'rotate', 0, c.at);
  return { plans: [twoPoint(c, 'rotate', start, n0(start + delta), 'persist')], notes: [] };
}

const OUT_SINE: Ease = { kind: 'tween', id: 2, left: 0, right: 1 };
const IN_SINE: Ease = { kind: 'tween', id: 3, left: 0, right: 1 };

function buildSwing(c: BuildCtx): Built {
  const { tx, p } = c;
  const amplitude = num(tx, p.params, 'amplitude', 15, (v) => v >= 0 && v <= 3600);
  const swings = num(tx, p.params, 'swings', 2, (v) => Number.isInteger(v) && v >= 1 && v <= 64);
  const dir = direction(tx, p.params);
  const settle = flag(p.params, 'settle', true);
  const n = swings * 4;
  const beats = evenBeats(tx, c.at, Beats.sub(c.end, c.at, tx.rounding), n);
  const line = c.line.rec.id;
  // Quarter k: 0 -> +A (Out Sine), +A -> 0 (In Sine), 0 -> -A (Out Sine), -A -> 0 (In Sine): an exact sinusoid.
  const shape = [0, 1, 0, -1];
  const points: PlanPoint[] = beats.map((t, k) => {
    const centre = c.relative ? 0 : numberAt(tx, line, 'rotate', 0, t);
    const peak = settle ? amplitude * (1 - k / n) : amplitude;
    return { t, vOut: n0(centre + dir * peak * (shape[k % 4] ?? 0)) };
  });
  const eases = beats.slice(0, -1).map((_, k) => (k % 2 === 0 ? OUT_SINE : IN_SINE));
  return { plans: [{ prop: 'rotate', points, eases, after: 'resume' }], notes: [] };
}

/**
 * Scale of the line at beat t as a key value; an absent track counts as the value that renders like it (scaleX 2 on
 * line.png lines), or null when no key value does (the thickness of a normal line).
 */
function scaleBase(c: BuildCtx, prop: 'scaleX' | 'scaleY', t: Beat): number | null {
  const o = infoOf(c.tx, c.line.rec.id, prop, 0);
  if (o.exists) return numberAt(c.tx, c.line.rec.id, prop, 0, t);
  return Defaults.leadInValue(prop, 0, c.line.kind, c.line.uiMover);
}

function buildPulse(c: BuildCtx): Built {
  const { tx, p } = c;
  const amount = num(tx, p.params, 'amount', 1.25, (v) => v > 0 && v <= 100);
  const count = num(tx, p.params, 'count', 1, (v) => Number.isInteger(v) && v >= 1 && v <= 64);
  const axes = choice<ShakeAxes>(tx, p.params, 'axes', 'both', ['both', 'x', 'y']);
  const props: ('scaleX' | 'scaleY')[] =
    axes === 'both' ? ['scaleX', 'scaleY'] : axes === 'x' ? ['scaleX'] : ['scaleY'];
  const beats = evenBeats(tx, c.at, Beats.sub(c.end, c.at, tx.rounding), count);
  const notes: MessageRef[] = [];
  const plans: SpanPlan[] = [];
  for (const prop of props) {
    if (scaleBase(c, prop, c.at) === null) {
      notes.push({ key: TEMPLATE_MSG.thicknessSkipped });
      continue;
    }
    const base = (t: Beat): number => scaleBase(c, prop, t) ?? 1;
    // Each pulse jumps to base * amount on its beat and eases back to the base by the next one.
    const points: PlanPoint[] = beats.map((t, k) => {
      const b = base(t);
      if (k === 0) return { t, vOut: n0(b * amount) };
      if (k === count) return { t, vOut: b };
      return { t, vIn: b, vOut: n0(b * amount) };
    });
    plans.push({ prop, points, eases: beats.slice(0, -1).map(() => c.ease), after: 'resume' });
  }
  return { plans, notes };
}

function buildShake(c: BuildCtx): Built {
  const { tx, p } = c;
  const amplitude = num(tx, p.params, 'amplitude', 20, (v) => v >= 0 && v <= 10000);
  const count = num(tx, p.params, 'count', 8, (v) => Number.isInteger(v) && v >= 2 && v <= 512);
  const axes = choice<ShakeAxes>(tx, p.params, 'axes', 'both', ['both', 'x', 'y']);
  const seed = num(tx, p.params, 'seed', 1);
  const settle = flag(p.params, 'settle', false);
  const beats = evenBeats(tx, c.at, Beats.sub(c.end, c.at, tx.rounding), count);
  const rng = createRng(seed);
  // Offsets are drawn for both axes whatever `axes` says, so switching axes keeps the other axis' pattern.
  const offsets = beats.map((_, k) => {
    const dx = (rng.next() * 2 - 1) * amplitude;
    const dy = (rng.next() * 2 - 1) * amplitude;
    if (k === 0 || k === count) return [0, 0] as const;
    const scale = settle ? 1 - k / count : 1;
    return [dx * scale, dy * scale] as const;
  });
  const line = c.line.rec.id;
  const props: ('moveX' | 'moveY')[] = axes === 'both' ? ['moveX', 'moveY'] : axes === 'x' ? ['moveX'] : ['moveY'];
  const plans = props.map((prop): SpanPlan => {
    const ai = prop === 'moveX' ? 0 : 1;
    const points = beats.map((t, k) => {
      const base = c.relative ? 0 : numberAt(tx, line, prop, 0, t);
      return { t, vOut: n0(base + (offsets[k]?.[ai] ?? 0)) };
    });
    return { prop, points, eases: beats.slice(0, -1).map(() => LINEAR), after: 'resume' };
  });
  return { plans, notes: [] };
}

function buildFlash(c: BuildCtx): Built {
  const { tx, p } = c;
  const raw = p.params.color;
  const flashColor = typeof raw === 'string' ? parseTemplateColor(raw) : null;
  if (flashColor === null) fail(tx, TEMPLATE_MSG.colorInvalid);
  const o = infoOf(tx, c.line.rec.id, 'color', 0);
  const notes: MessageRef[] = [];
  let back: RGB;
  if (o.exists) back = layerValue(tx, c.line.rec.id, 'color', 0, c.end) as RGB;
  else if (c.line.kind === 'normal' && !c.line.uiMover) {
    back = DEFAULT_LINE_COLOR;
    notes.push({ key: TEMPLATE_MSG.colorFixed });
  } else back = [255, 255, 255];
  const plan = twoPoint(c, 'color', flashColor, back, 'resume');
  // A new colour track also holds the colour the flash returns to from beat 0, so the line looks the same before
  // and after the flash (before its first colour key Phira would show white).
  return { plans: [o.exists ? plan : { ...plan, leadIn: back }], notes };
}

/** Slice ease of one orbit piece: `fn` 'sin' -> Out Sine, 'cos' -> In Sine, on the part [l, r] of the curve. */
function sineSlice(fn: 'sin' | 'cos', l: number, r: number): Ease {
  const id = fn === 'sin' ? 2 : 3;
  const left = Math.min(Math.max(l, 0), 1);
  const right = Math.min(Math.max(r, 0), 1);
  if (left <= 1e-9 && right >= 1 - 1e-9) return { kind: 'tween', id, left: 0, right: 1 };
  const e: Ease = { kind: 'tween', id, left: tidy(left), right: tidy(right) };
  return tidy(left) < tidy(right) && EaseApi.validateFor(e, 'general') === null ? e : LINEAR;
}

const HALF_PI = Math.PI / 2;

/**
 * Ease of one orbit piece of the X (cos) or Y (sin) component between the angles a and b (radians, same quarter).
 * With w the increasing parameter of the quarter (theta = base + s * w * pi/2), each component is a multiple of
 * sin(w pi/2) (Out Sine) or cos(w pi/2) = 1 - In Sine (In Sine), so a slice [w(a), w(b)] of that curve, renormalised
 * by Phira, reproduces it exactly. Returns the ease and the largest deviation from the circle caused by clamping
 * (an angle range that crosses a quarter by a snapping step).
 */
function orbitEase(component: 'x' | 'y', a: number, b: number): { readonly ease: Ease; readonly clampRad: number } {
  const mid = (a + b) / 2;
  const q = Math.floor(mid / HALF_PI);
  const increasing = b >= a;
  const base = increasing ? q * HALF_PI : (q + 1) * HALF_PI;
  const s = increasing ? 1 : -1;
  const wa = ((a - base) * s) / HALF_PI;
  const wb = ((b - base) * s) / HALF_PI;
  const clampRad = Math.max(0, -wa, wb - 1) * HALF_PI;
  // cos(base + s w pi/2) and sin(...) reduce to +-sin(w pi/2) or +-cos(w pi/2) because base is a multiple of pi/2.
  const baseIsVertical = Math.abs(Math.cos(base)) < 0.5;
  const fn: 'sin' | 'cos' = component === 'x' ? (baseIsVertical ? 'sin' : 'cos') : baseIsVertical ? 'cos' : 'sin';
  return { ease: sineSlice(fn, wa, wb), clampRad };
}

/** Finest beat grid orbit keys snap to. */
const ORBIT_SNAP = 1920;

/**
 * Factor from RPE Y px to "isotropic" px (RPE X px): Phira maps a line's own translation to world units as
 * (moveX / 675, moveY / 450 / ar) (rendering.md §3.1), and one world unit is as long on both screen axes, so one RPE Y
 * px is 675 / (450 * ar) = 1.5 / ar RPE X px on screen. A circle on screen is a circle in (X, Y * 1.5 / ar).
 */
function isoYFactor(tx: TxBuilder): number {
  const ar = tx.doc.pkg.aspectRatio;
  return 1.5 / (Number.isFinite(ar) && ar > 0 ? ar : 16 / 9);
}

function buildOrbit(c: BuildCtx): Built {
  const { tx, p } = c;
  const line = c.line.rec.id;
  const turns = num(tx, p.params, 'turns', 1, (v) => v > 0 && v <= 256);
  const dir = direction(tx, p.params);
  const radiusParam = num(tx, p.params, 'radius', 0, (v) => v >= 0);
  const notes: MessageRef[] = c.line.rec.parent === null ? [{ key: TEMPLATE_MSG.noParent }] : [];
  // Position relative to the parent's origin (the line's own coordinates) at the start. The circle is built in
  // isotropic px (X, Y * k) so it is round on screen whatever the aspect ratio; the radius is in RPE X px.
  const k = isoYFactor(tx);
  const x0 = c.relative ? sumLayers(tx, line, 'moveX', c.at, null) : numberAt(tx, line, 'moveX', 0, c.at);
  const y0 = c.relative ? sumLayers(tx, line, 'moveY', c.at, null) : numberAt(tx, line, 'moveY', 0, c.at);
  const dist = Math.hypot(x0, y0 * k);
  const r = radiusParam > 0 ? radiusParam : dist > 0 ? dist : 200;
  const theta0 = dist > 0 ? Math.atan2(y0 * k, x0) : 0;
  // Clockwise on screen (Y up) = decreasing angle.
  const total = -dir * 2 * Math.PI * turns;
  const atF = Beats.toF64(c.at);
  const durF = Beats.toF64(c.end) - atF;
  // Key fractions: the start, every quarter-turn boundary strictly inside, the end.
  const fractions: number[] = [0];
  const lo = Math.min(theta0, theta0 + total);
  const hi = Math.max(theta0, theta0 + total);
  const bounds: number[] = [];
  for (let k = Math.floor(lo / HALF_PI) + 1; k * HALF_PI < hi; k++) bounds.push(k * HALF_PI);
  if (total < 0) bounds.reverse();
  for (const th of bounds) fractions.push((th - theta0) / total);
  fractions.push(1);
  const beats: Beat[] = [];
  for (const f of fractions) {
    const t = f === 0 ? c.at : f === 1 ? c.end : Beats.fromF64(atF + f * durF, ORBIT_SNAP);
    const prev = beats[beats.length - 1];
    if (prev !== undefined && !Beats.lt(prev, t)) continue;
    if (f !== 1 && !Beats.lt(t, c.end)) continue;
    beats.push(t);
  }
  const angles = beats.map((t) => theta0 + (total * (Beats.toF64(t) - atF)) / durF);
  let clamp = 0;
  const plans = (['moveX', 'moveY'] as const).map((prop): SpanPlan => {
    const comp = prop === 'moveX' ? 'x' : 'y';
    const origin = prop === 'moveX' ? x0 : y0;
    // Y is scaled back from isotropic to RPE Y px: a linear scale, so each piece stays a slice of a sine curve.
    const valueAt = (th: number): number =>
      (comp === 'x' ? r * Math.cos(th) : (r * Math.sin(th)) / k) - (c.relative ? origin : 0);
    const points = beats.map((t, k) => ({ t, vOut: n0(valueAt(angles[k] ?? theta0)) }));
    const eases = beats.slice(0, -1).map((_, k) => {
      const o = orbitEase(comp, angles[k] ?? 0, angles[k + 1] ?? 0);
      clamp = Math.max(clamp, o.clampRad);
      return o.ease;
    });
    return { prop, points, eases, after: 'persist' };
  });
  return { plans, notes, maxError: r * (1 - Math.cos(clamp)) };
}

// ---------------------------------------------------------------------------------------------------------------
// Writing a span with Core.keys
// ---------------------------------------------------------------------------------------------------------------

function keyAt(tx: TxBuilder, track: TrackId, t: Beat): KeyId | undefined {
  for (const id of tx.idx.keysByTrack(track)) {
    const k = tx.doc.keys.get(id);
    if (k !== undefined && Beats.eq(k.t, t)) return id;
  }
  return undefined;
}

function requireKeyAt(tx: TxBuilder, track: TrackId, t: Beat): KeyId {
  return keyAt(tx, track, t) ?? fail(tx, TEMPLATE_MSG.lineMissing);
}

function sameEase(a: Ease, b: Ease): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'tween' && b.kind === 'tween') return a.id === b.id && a.left === b.left && a.right === b.right;
  if (a.kind === 'bezier' && b.kind === 'bezier') return a.p.every((v, i) => v === b.p[i]);
  return true;
}

/**
 * Writes one span on (line, prop, layer): split the edges, remove the keys inside, insert the plan's keys, set the
 * eases, then the values, and make the end value stay after the span when the plan says so. Returns the number of
 * keys of the span.
 */
function writeSpan(tx: TxBuilder, line: LineId, layer: number, plan: SpanPlan): number {
  const first = plan.points[0];
  const last = plan.points[plan.points.length - 1];
  if (first === undefined || last === undefined || plan.points.length < 2) return 0;
  const owner = { kind: 'line' as const, line, prop: plan.prop, layer };
  let track = tx.idx.trackOfOwner(owner);
  const created = track === undefined;
  // A new track has no curve after the span to return to: its end value stays.
  const after = created ? 'persist' : plan.after;
  if (track === undefined) {
    const o = infoOf(tx, line, plan.prop, layer);
    track = Core.keys.ensureTrack(tx, { owner, value: neutralValue(o.info), at: first.t });
  }
  Core.keys.insertKey(tx, { track, t: first.t });
  Core.keys.insertKey(tx, { track, t: last.t });
  const inside: KeyId[] = [];
  for (const id of tx.idx.keysByTrack(track)) {
    const k = tx.doc.keys.get(id);
    if (k !== undefined && Beats.lt(first.t, k.t) && Beats.lt(k.t, last.t)) inside.push(id);
  }
  if (inside.length > 0) Core.keys.deleteKeys(tx, { keys: inside });
  for (const pt of plan.points.slice(1, -1)) Core.keys.insertKey(tx, { track, t: pt.t, value: pt.vOut });
  const ids = plan.points.map((pt) => requireKeyAt(tx, track, pt.t));
  const starts = ids.slice(0, -1);
  if (starts.some((id) => (tx.doc.keys.get(id)?.link ?? 0) !== 0)) Core.keys.unlinkSegments(tx, { segments: starts });
  // Eases first (a Stay between two span keys would refuse an arrive value), grouped by equal curves.
  const groups: { ease: Ease; ids: KeyId[] }[] = [];
  starts.forEach((id, i) => {
    const ease = plan.eases[i] ?? LINEAR;
    const g = groups.find((x) => sameEase(x.ease, ease));
    if (g === undefined) groups.push({ ease, ids: [id] });
    else g.ids.push(id);
  });
  for (const g of groups) Core.keys.setEase(tx, { segments: g.ids, ease: g.ease });
  plan.points.forEach((pt, i) => {
    const id = ids[i] as KeyId;
    if (i === 0) {
      Core.keys.setKeyValues(tx, { keys: [id], part: 'out', value: pt.vOut });
      return;
    }
    if (i === plan.points.length - 1) {
      Core.keys.setKeyValues(tx, { keys: [id], part: after === 'persist' ? 'both' : 'in', value: pt.vOut });
      return;
    }
    if (pt.vIn === undefined) Core.keys.setKeyValues(tx, { keys: [id], part: 'both', value: pt.vOut });
    else {
      Core.keys.setKeyValues(tx, { keys: [id], part: 'in', value: pt.vIn });
      Core.keys.setKeyValues(tx, { keys: [id], part: 'out', value: pt.vOut });
    }
  });
  const endId = ids[ids.length - 1] as KeyId;
  if (after === 'persist' && tx.idx.nextKey(endId) !== undefined) {
    Core.keys.setEase(tx, { segments: [endId], ease: HOLD });
  }
  if (
    created &&
    plan.leadIn !== undefined &&
    Beats.lt(Beats.ZERO, first.t) &&
    keyAt(tx, track, Beats.ZERO) === undefined
  ) {
    // Stay from beat 0 on the lead-in value; the span's first key then jumps to its own value (holds arrive as jumps).
    const lead = Core.keys.insertKey(tx, { track, t: Beats.ZERO, value: plan.leadIn });
    Core.keys.setEase(tx, { segments: [lead.key], ease: HOLD });
    return plan.points.length + 1;
  }
  return plan.points.length;
}

/**
 * Imported lead-ins: where a track's lead-in rule is 'extrapolate' and its first key lies after beat 0, Phira shows
 * the first segment continued backwards before that key. A base-mode span starting at or before the first key would
 * make the template's own first segment the one Phira continues, so the line would change before the span (a fade
 * in hiding it from beat 0, a spin turning it before the span starts). Before anything is written, every such
 * layer-0 track the template rewrites gets a key at beat 0 that continues its lead-in (motionTime.ts
 * `explicitLeadIn`: exact for a straight or Stay first segment; a curved one becomes straight there, measured and
 * noted as Core notes it), which clears `importedLeadIn` and turns [0, first key) into a segment the span leaves
 * alone. Runs while the line is still X/Y-linked: the other position axis then gets its key at beat 0 too (link.ts
 * `alignXyBeats`, shape-preserving), as Core's own lead-in operations do, so a linked line gets the same curves as
 * an unlinked one. Returns true when a key was inserted.
 */
function explicitLeadIns(tx: TxBuilder, line: LineCtx, props: ReadonlySet<LineProp>, at: Beat): boolean {
  if (!Beats.lt(Beats.ZERO, at)) return false;
  const id = line.rec.id;
  const unlinked = new Set<number>();
  let edited: SplitOn | null = null;
  let partner: SplitReport = EXACT_SPLIT;
  const positions = new Set<LineProp>();
  let inserted = false;
  for (const prop of props) {
    const o = infoOf(tx, id, prop, 0);
    if (!o.exists) continue;
    const keys = readKeys(tx, o.info.track.id);
    const count = keys.length;
    const report = explicitLeadIn(keys, at, curveCtx(tx, o.info), unlinked);
    if (keys.length === count) continue;
    commitKeys(tx, o.info, keys);
    edited = collectSplit(edited, o.info, report);
    if (isPosition(prop)) positions.add(prop);
    inserted = true;
  }
  if (positions.size > 0 && tx.doc.lines.get(id)?.editor.xyLinked === true) {
    const r = alignXyBeats(
      tx,
      { line: id, layer: 0, spans: [], beats: new Map([[Beats.key(Beats.ZERO), Beats.ZERO]]), edited: positions },
      unlinked,
    );
    edited = worseSplitOn(edited, r.edited);
    partner = worseSplit(partner, r.partner);
  }
  cleanLinks(tx, unlinked);
  noteWorstSplit(tx, edited);
  notePartnerSplit(tx, partner);
  return inserted;
}

// ---------------------------------------------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------------------------------------------

function build(c: BuildCtx): Built {
  switch (c.p.template) {
    case 'fadeIn':
      return buildFade(c, false);
    case 'fadeOut':
      return buildFade(c, true);
    case 'slideIn':
      return buildSlide(c, true);
    case 'slideOut':
      return buildSlide(c, false);
    case 'moveTo':
      return buildMoveTo(c);
    case 'spin':
      return buildSpin(c);
    case 'swing':
      return buildSwing(c);
    case 'pulse':
      return buildPulse(c);
    case 'shake':
      return buildShake(c);
    case 'flash':
      return buildFlash(c);
    case 'orbitParent':
      return buildOrbit(c);
  }
}

/** The end beat of a template's span (at + duration). */
export function templateEnd(p: Pick<TemplateParams, 'at' | 'duration'>): Beat {
  return Beats.add(p.at, p.duration);
}

const POSITION: readonly LineProp[] = ['moveX', 'moveY'];

function isPosition(prop: LineProp): boolean {
  return prop === 'moveX' || prop === 'moveY';
}

/** Lowest layer index (0..MAX_UI_LAYERS - 1) without a track on every one of `props`, or null. */
function commonFreeLayer(tx: TxBuilder, line: LineId, props: readonly LineProp[]): number | null {
  for (let layer = 0; layer < MAX_UI_LAYERS; layer++) {
    if (props.every((prop) => tx.idx.track(line, prop, layer) === undefined)) return layer;
  }
  return null;
}

/**
 * New offset layers ("Apply as a new layer"). Position goes on ONE layer index for both axes (the lowest one free
 * on both; on an X/Y-linked line free on both axes even when one axis moves, so the link pairs the new layer with
 * an empty one): each axis starts as a key of 0 at beat 0 that becomes a Stay once the span is written
 * (`holdNewLayer`), exactly what `Core.keys.addLayer` writes. Other properties use `Core.keys.addLayer`. Returns
 * the layer of each property and the properties whose layer this function created at a chosen index.
 */
function newLayers(
  tx: TxBuilder,
  line: LineCtx,
  props: ReadonlySet<LineProp>,
): { readonly layerOf: Map<LineProp, number>; readonly chosen: Set<LineProp> } {
  const layerOf = new Map<LineProp, number>();
  const chosen = new Set<LineProp>();
  const id = line.rec.id;
  for (const prop of props) if (!PROP_SPECS[prop].layered) fail(tx, TEMPLATE_MSG.notLayered);
  const pos = POSITION.filter((prop) => props.has(prop));
  const common = pos.length === 0 ? null : commonFreeLayer(tx, id, line.rec.editor.xyLinked ? POSITION : pos);
  for (const prop of props) {
    if (common !== null && isPosition(prop)) {
      Core.keys.ensureTrack(tx, {
        owner: { kind: 'line', line: id, prop, layer: common },
        value: 0,
        at: Beats.ZERO,
      });
      layerOf.set(prop, common);
      chosen.add(prop);
    } else layerOf.set(prop, Core.keys.addLayer(tx, { line: id, prop }));
  }
  return { layerOf, chosen };
}

/** Makes the beat-0 key of a new offset layer a Stay (it holds 0 up to the span; a span at beat 0 owns that key). */
function holdNewLayer(tx: TxBuilder, line: LineId, prop: LineProp, layer: number, at: Beat): void {
  if (!Beats.lt(Beats.ZERO, at)) return;
  const track = tx.idx.track(line, prop, layer);
  const first = track === undefined ? undefined : keyAt(tx, track, Beats.ZERO);
  if (first !== undefined && tx.doc.keys.get(first)?.ease.kind !== 'hold') {
    Core.keys.setEase(tx, { segments: [first], ease: HOLD });
  }
}

/**
 * Re-establishes the X/Y link after both axes were written independently (see `applyTemplate`): links the line
 * again and gives both axes of each written layer keys at each other's beats inside the span (shape-preserving
 * splits; values and curves stay per axis; keys outside the span are left as they are). A layer where the template
 * created a position track (a new layer, an axis the line did not have) is aligned over all its keys, as
 * `Core.keys.ensureTrack` does for a created axis, and an absent other axis is created as a constant 0. Split notes
 * as Core's (`noteWorstSplit` for the written axes, `notePartnerSplit` for the other one).
 */
function relinkXy(
  tx: TxBuilder,
  line: LineId,
  at: Beat,
  end: Beat,
  written: ReadonlyMap<number, ReadonlySet<LineProp>>,
  tracksBefore: ReadonlySet<TrackId>,
): void {
  Core.lines.patchLineEditor(tx, { id: line, patch: { xyLinked: true } });
  const unlinked = new Set<number>();
  let edited: SplitOn | null = null;
  let partner: SplitReport = EXACT_SPLIT;
  for (const [layer, props] of written) {
    const created = POSITION.some((prop) => {
      const id = tx.idx.track(line, prop, layer);
      return id !== undefined && !tracksBefore.has(id);
    });
    const r = created
      ? alignXyAll(tx, line, layer, props, unlinked)
      : alignXyBeats(
          tx,
          {
            line,
            layer,
            spans: [[at, end]],
            beats: new Map(),
            edited: new Set(props),
          },
          unlinked,
        );
    edited = worseSplitOn(edited, r.edited);
    partner = worseSplit(partner, r.partner);
  }
  cleanLinks(tx, unlinked);
  noteWorstSplit(tx, edited);
  notePartnerSplit(tx, partner);
}

export const applyTemplate: ToolsApi['applyTemplate'] = (tx, p): ToolReport => {
  const spec = TEMPLATE_SPECS[p.template];
  const line = lineCtx(tx, p.line);
  if (!(Beats.cmp(p.duration, Beats.ZERO) > 0) || Beats.cmp(p.at, Beats.ZERO) < 0)
    fail(tx, TEMPLATE_MSG.durationInvalid);
  if (p.asNewLayer && !spec.layered) fail(tx, TEMPLATE_MSG.notLayered);
  const end = Beats.add(p.at, p.duration, tx.rounding);
  const ease = spec.usesEase ? p.ease : LINEAR;
  const tracksBefore = new Set([...tx.idx.tracksOfLine(p.line).values()].flat());
  // Everything is read from the document BEFORE anything is written.
  let built = build({ tx, p, line, at: p.at, end, relative: p.asNewLayer, ease });
  if (!p.asNewLayer && explicitLeadIns(tx, line, new Set(built.plans.map((pl) => pl.prop)), p.at)) {
    // Read again from the explicit lead-ins: a curved one that became straight moved the values a plan starts or
    // ends on, which the span must meet without a jump.
    built = build({ tx, p, line: lineCtx(tx, p.line), at: p.at, end, relative: false, ease });
  }
  const notes: MessageRef[] = [...built.notes];
  const writtenProps = new Set(built.plans.map((pl) => pl.prop));
  const posWritten = POSITION.filter((prop) => writtenProps.has(prop));
  // X/Y link: Core.keys mirrors inserting, deleting and re-easing a key onto the other axis at the same beat, so
  // writing the X plan and then the Y plan would delete X's span keys again, re-insert them with values from X's
  // merged curve and give X the curves of Y (an orbit off its circle, a shake that only moves Y), and a template
  // that moves one axis would delete the other axis' keys inside the span. Both axes are written UNLINKED (each
  // exactly as on an unlinked line), then the link is restored with keys at shared beats (`relinkXy`).
  const linked = line.rec.editor.xyLinked && posWritten.length > 0;
  if (linked && posWritten.length === 1) notes.push({ key: TEMPLATE_MSG.xyLinked });
  if (linked) Core.lines.patchLineEditor(tx, { id: p.line, patch: { xyLinked: false } });
  const layers = p.asNewLayer ? newLayers(tx, line, writtenProps) : null;
  const layerOf = (prop: LineProp): number => layers?.layerOf.get(prop) ?? 0;
  let changed = 0;
  for (const plan of built.plans) {
    changed += writeSpan(tx, p.line, layerOf(plan.prop), plan);
    if (layers?.chosen.has(plan.prop) === true) holdNewLayer(tx, p.line, plan.prop, layerOf(plan.prop), p.at);
  }
  if (linked) {
    const written = new Map<number, Set<LineProp>>();
    for (const prop of posWritten) {
      const layer = layerOf(prop);
      const set = written.get(layer) ?? new Set<LineProp>();
      set.add(prop);
      written.set(layer, set);
    }
    relinkXy(tx, p.line, p.at, end, written, tracksBefore);
  }
  tx.setFocus({ beat: p.at, line: p.line });
  return {
    changed,
    ...(built.maxError === undefined ? {} : { maxError: built.maxError }),
    ...(notes.length === 0 ? {} : { notes }),
  };
};
